import * as React from 'react'

import { cn } from '../../utils/cn'
import { Banner } from './banner'
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from './table'
import { TableCard, TableCardField, TableCardList } from './table-card'

export interface ResponsiveTableColumn<T> {
  /** Column identity, and the card field's React key. */
  key: string
  /** Header cell on the table, field label on the card. */
  header: string
  /** Renders in both treatments, so a cell and its card field can't drift apart. */
  cell: (item: T) => React.ReactNode
  headClassName?: string
  cellClassName?: string
  /** Skips the card field — for a column already folded into `cardTitle`. */
  hideOnCard?: boolean
}

export interface ResponsiveTableProps<T> {
  /** The rows to render — already searched and paged by the caller. */
  items: T[]
  columns: ResponsiveTableColumn<T>[]
  getRowId: (item: T) => string
  /** The identifying field, leading the card the way the first column leads the row. */
  cardTitle: (item: T) => React.ReactNode
  /** Opens the record: the row's click target and the card title's button. */
  onRowClick?: (item: T) => void
  /** Edit/delete controls. Passing it adds the trailing actions column. */
  rowActions?: (item: T) => React.ReactNode
  /** Stands in for the rows when `items` is empty (e.g. nothing matched the search). */
  empty?: React.ReactNode
  /** Action bar below the rows: `TableCaption` on the table, a plain block on cards. */
  caption?: React.ReactNode
  /** Applied to both treatments, so layout tweaks stay in one place. */
  className?: string
  /** What this row still needs, as a red banner on its card, or under its row on the table. */
  rowIssue?: (item: T) => string | null | undefined
}

function RowIssue({ issue }: { issue: string }) {
  return (
    <Banner variant="error" small align="center">
      {issue}
    </Banner>
  )
}

/** True once the table is wider than the space it has, so showing it would mean a sideways scrollbar. */
function useTableOverflows(rowsRef: React.RefObject<HTMLDivElement | null>) {
  const [overflows, setOverflows] = React.useState(false)

  React.useLayoutEffect(() => {
    const rows = rowsRef.current
    const table = rows?.querySelector<HTMLElement>('[data-slot=table]')
    if (!rows || !table) return
    // Measured while hidden too: a zero-height box still lays the table out at the width it has.
    const measure = () => setOverflows(rows.clientWidth > 0 && table.scrollWidth > rows.clientWidth + 1)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(rows)
    observer.observe(table)
    return () => observer.disconnect()
  }, [rowsRef])

  return overflows
}

/**
 * One column list, two treatments: the `Table` at `lg` and up, a `TableCard` stack
 * below it — where a table can only scroll sideways. Both render and the breakpoint
 * picks; `useIsMobile` resolves in an effect, which would paint the wide table on a
 * phone and then snap to cards. A table too wide for its container at `lg` and up (a
 * dialog, say) swaps to the cards too: one column, or two where the space is wide enough.
 */
export function ResponsiveTable<T>({
  items,
  columns,
  getRowId,
  cardTitle,
  onRowClick,
  rowActions,
  empty,
  caption,
  className,
  rowIssue,
}: ResponsiveTableProps<T>) {
  const cardColumns = columns.filter((column) => !column.hideOnCard)
  const isEmpty = items.length === 0
  const rowsRef = React.useRef<HTMLDivElement>(null)
  const overflows = useTableOverflows(rowsRef)

  return (
    <>
      <div
        data-slot="responsive-table-cards"
        className={cn('@container', !overflows && 'lg:hidden', className)}
      >
        {isEmpty ? (
          empty ? (
            <div className="text-muted-foreground py-5 text-center text-sm">{empty}</div>
          ) : null
        ) : (
          <TableCardList className={cn(overflows && 'lg:@2xl:grid lg:@2xl:grid-cols-2')}>
            {items.map((item) => {
              const issue = rowIssue?.(item)
              return (
                <TableCard
                  key={getRowId(item)}
                  title={cardTitle(item)}
                  actions={rowActions?.(item)}
                  onOpen={onRowClick ? () => onRowClick(item) : undefined}
                  notice={issue ? <RowIssue issue={issue} /> : null}
                >
                  {cardColumns.map((column) => (
                    <TableCardField key={column.key} label={column.header} value={column.cell(item)} />
                  ))}
                </TableCard>
              )
            })}
          </TableCardList>
        )}
        {caption ? <div className="text-muted-foreground mt-4 text-sm">{caption}</div> : null}
      </div>

      <div
        ref={rowsRef}
        data-slot="responsive-table-rows"
        aria-hidden={overflows || undefined}
        // Kept laid out but unseen while the cards stand in, so it can say when it fits again.
        className={cn('hidden lg:block', overflows && 'invisible h-0 overflow-hidden', className)}
      >
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.key} className={cn('font-semibold', column.headClassName)}>
                  {column.header}
                </TableHead>
              ))}
              {rowActions ? <TableHead className="font-semibold" /> : null}
            </TableRow>
          </TableHeader>

          <TableBody>
            {isEmpty
              ? empty && (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + (rowActions ? 1 : 0)}
                      className="text-muted-foreground pt-5 text-center"
                    >
                      {empty}
                    </TableCell>
                  </TableRow>
                )
              : items.map((item) => {
                  const issue = rowIssue?.(item)
                  return (
                    <React.Fragment key={getRowId(item)}>
                      <TableRow
                        className={cn(onRowClick && 'cursor-pointer')}
                        onClick={onRowClick ? () => onRowClick(item) : undefined}
                      >
                        {columns.map((column) => (
                          <TableCell key={column.key} className={column.cellClassName}>
                            {column.cell(item)}
                          </TableCell>
                        ))}
                        {rowActions ? (
                          // The row opens the record, so actions swallow their own clicks.
                          <TableCell onClick={(event) => event.stopPropagation()}>
                            <div className="flex items-center gap-2">{rowActions(item)}</div>
                          </TableCell>
                        ) : null}
                      </TableRow>
                      {issue ? (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={columns.length + (rowActions ? 1 : 0)} className="pt-0">
                            <RowIssue issue={issue} />
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </React.Fragment>
                  )
                })}
          </TableBody>

          {caption ? <TableCaption>{caption}</TableCaption> : null}
        </Table>
      </div>
    </>
  )
}
